/**
 * The answer text for an FAQ built from a content block ("What are the menu
 * highlights at X?"). Answers render as HTML, on the page and in the FAQPage
 * JSON-LD.
 *
 * Two or three short items read well as one sentence. Longer lists, or items
 * that carry their own commas ("Sushi and sashimi, eighteen pieces"), used to
 * be glued with semicolons into one long sentence ("include A single set
 * menu…; Tanjia…; and Seasonal fruit plate…"). Those now get the template's
 * own opening as a lead line and the items as a list (lab round 26, item 197).
 */

type Locale = "en" | "fr" | "ar" | "es" | "it" | "de" | "nl";
export type BlockFaqTemplate = (subject: string, list: string) => string;

const LIST_FROM = 4;
const MARK = "\u0000";

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function cleanItems(items: string[]): string[] {
  // Blocks hold short noun phrases ("Rooftop pool") or full sentences
  // ("Outdoor pool set in the gardens."). Drop a trailing full stop so the
  // answer never reads "…a wine cellar..".
  return items.map((s) => s.trim().replace(/\s*[.。]\s*$/, "")).filter(Boolean);
}

function joinSentence(clean: string[], locale: Locale): string {
  if (clean.length === 1) return clean[0];
  // Only reached with commas inside items when the template has no lead to
  // list under; semicolons then keep the items apart.
  const hasComma = clean.some((s) => /[,،]/.test(s));
  const sep = locale === "ar" ? (hasComma ? "؛ " : "، ") : hasComma ? "; " : ", ";
  const and =
    locale === "ar" ? " و" : locale === "fr" ? " et " : locale === "es" ? " y " : locale === "it" ? " e " : locale === "de" ? " und " : locale === "nl" ? " en " : " and ";
  if (clean.length === 2) return `${clean[0]}${and}${clean[1]}`;
  const head = clean.slice(0, -1).join(sep);
  const tail = clean[clean.length - 1];
  if (locale === "ar") return `${head}${sep}و${tail}`;
  // Only English takes the serial comma.
  return locale === "en" ? `${head}${hasComma ? ";" : ","}${and}${tail}` : `${head}${and}${tail}`;
}

/** The template's words before the list, ending in a colon; null when the list is not at the end. */
function leadOf(template: BlockFaqTemplate, subject: string, locale: Locale): string | null {
  const probe = template(subject, MARK);
  const at = probe.indexOf(MARK);
  if (at < 0 || probe.slice(at + MARK.length).replace(/[.\s]/g, "") !== "") return null;
  const head = probe.slice(0, at).trimEnd();
  if (/[:：]$/.test(head)) return head;
  return locale === "fr" ? `${head} :` : `${head}:`;
}

export function blockFaqAnswer(template: BlockFaqTemplate, subject: string, items: string[], locale: Locale): string {
  const clean = cleanItems(items).map(escapeHtml);
  if (clean.length === 0) return "";
  const subj = escapeHtml(subject);
  const asList = clean.length >= LIST_FROM || (clean.length > 1 && clean.some((s) => /[,،]/.test(s)));
  if (asList) {
    const lead = leadOf(template, subj, locale);
    if (lead) return `<p>${lead}</p><ul>${clean.map((s) => `<li>${s.charAt(0).toUpperCase()}${s.slice(1)}</li>`).join("")}</ul>`;
  }
  return template(subj, joinSentence(clean, locale));
}
